import {
  ensureFileToken,
  generateFilePreviewUrlWithToken,
  isImageFile,
  openFilePreview,
  openFilePreviewByPath,
} from "@/lib/utils/filePreview";
import { Thumbnail } from "@/components/file-preview";
import styles from "../../styles/applicationDetail.module.css";
import type {
  GovIdAttachment,
  GovernmentIdAttachmentPreviewProps,
} from "../../types";

type GovIdPreviewItem = {
  attachment: GovIdAttachment;
  fileType: "ID_PROOF" | "ID_PROOF_BACK";
};

/**
 * Build secure preview URLs for image government-ID sides so file-handlers
 * can show left/right nav (Applicant Signature + supporting-docs parity).
 */
export async function buildGovernmentIdPreviewFilesList(
  items: GovIdPreviewItem[],
  applicantId: string,
): Promise<string[] | undefined> {
  const imageItems = items.filter((item) => {
    if (!item.attachment.attachment_id) return false;
    const name = item.attachment.filename || item.attachment.filepath || "";
    return isImageFile(name);
  });
  if (imageItems.length <= 1) return undefined;

  const token = await ensureFileToken();
  if (!token?.trim()) return undefined;

  return imageItems.map((item) =>
    generateFilePreviewUrlWithToken(
      item.attachment.attachment_id as string | number,
      item.fileType,
      applicantId,
      "APPLICATION",
      token,
    ),
  );
}

export function GovernmentIdAttachmentPreview({
  attachment,
  fileType,
  applicantId,
  previewGroup,
}: GovernmentIdAttachmentPreviewProps) {
  if (!attachment?.filepath) return null;

  const filePath = attachment.filepath;
  const fileName = attachment.filename || "Document";
  const showThumbnail = isImageFile(fileName) || isImageFile(filePath);
  const group: GovIdPreviewItem[] =
    previewGroup && previewGroup.length > 0
      ? previewGroup
      : [{ attachment, fileType }];

  const openPreview = async () => {
    const filesList = await buildGovernmentIdPreviewFilesList(group, applicantId);

    if (attachment.attachment_id) {
      await openFilePreview(
        attachment.attachment_id,
        fileType,
        fileName,
        applicantId,
        "APPLICATION",
        filesList,
      );
      return;
    }
    openFilePreviewByPath(filePath, fileName, filesList);
  };

  return (
    <div className={styles.governmentIdAttachmentPreview}>
      <button
        type="button"
        className={`${styles.link} ${styles.governmentIdAttachmentLink}`}
        onClick={() => void openPreview()}
      >
        {fileName}
      </button>
      {showThumbnail ? (
        <div className={styles.governmentIdThumbnailWrap}>
          <Thumbnail
            filepath={filePath}
            attachmentId={attachment.attachment_id}
            fileType={fileType}
            fileName={fileName}
            refId={applicantId}
            module="APPLICATION"
            onClick={() => void openPreview()}
          />
        </div>
      ) : null}
    </div>
  );
}
